import type { ChartSeries } from '../../shared/charts/series';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { formatWalletMoney, type WalletLedgerEntry } from './types';

export interface WalletBalanceSeriesNames {
  readonly available: string;
  readonly reserved: string;
}

export interface WalletBalanceChartData {
  readonly categories: readonly string[];
  readonly series: readonly ChartSeries[];
}

/**
 * Maps one page of immutable ledger entries onto the two balance series the chart plots.
 *
 * `availableAfter` and `reservedAfter` are running balances the backend already computed for each entry,
 * so this reorders and formats server values — it never derives a financial figure. Series names arrive
 * as arguments so this stays a plain function, testable without React or i18n context.
 */
export function toWalletBalanceSeries(
  entries: readonly WalletLedgerEntry[],
  names: WalletBalanceSeriesNames,
  language: TimeLanguage,
): WalletBalanceChartData {
  // toSorted is stable, so entries sharing one effectiveAt keep the order the API returned them in.
  const chronological = entries.toSorted(
    (left, right) => Date.parse(left.effectiveAt) - Date.parse(right.effectiveAt),
  );

  const categories = chronological.map((entry) => {
    const formatted = formatTimestamp(entry.effectiveAt, 'Asia/Bangkok', language);
    return `${formatted.display} (${formatted.originalOffset})`;
  });

  const series: readonly ChartSeries[] = [
    {
      labels: chronological.map((entry) =>
        formatWalletMoney(entry.availableAfter, entry.currencyCode, entry.currencyMinorUnit),
      ),
      name: names.available,
      // Number() drives pixel geometry only; the paired label keeps the exact decimal string.
      values: chronological.map((entry) => Number(entry.availableAfter)),
    },
    {
      labels: chronological.map((entry) =>
        formatWalletMoney(entry.reservedAfter, entry.currencyCode, entry.currencyMinorUnit),
      ),
      name: names.reserved,
      values: chronological.map((entry) => Number(entry.reservedAfter)),
    },
  ];

  return { categories, series };
}
